import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import {
  ProductTryOnFields,
  ProductTryOnRow,
  ShopProductService
} from 'src/app/connect-api/api/shop/shop-product.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  TRY_ON_CALIBRATION_OPTIONS,
  TRY_ON_DEFAULT_MODE,
  TRY_ON_INTENSITY_MAX,
  TRY_ON_INTENSITY_MIN,
  TRY_ON_LIVE_ENABLED,
  TRY_ON_SHADES_MAX,
  TRY_ON_SHADE_NAME_MAX,
  TryOnCalibration,
  TryOnCategory,
  TryOnFinish,
  TryOnLegacyToneFields,
  TryOnMode,
  TryOnOption,
  TryOnRenderFields,
  TryOnShadeDraft,
  buildSingleSkuToneSetInputs,
  buildTryOnProductSetInput,
  buildTryOnRowSetInput,
  buildTryOnShadesInput,
  canAddTryOnShade,
  coerceTryOnMode,
  getTryOnCategoryOptions,
  getTryOnEnableBlockReason,
  getTryOnFinishOptionsForCategory,
  getTryOnModeOptionsForCategory,
  getTryOnRowErrors,
  getTryOnShadeErrors,
  getTryOnShadesListErrors,
  isSameLegacyTone,
  isTryOnFinishAllowed,
  isTryOnFinishRequired,
  isTryOnPhotoSupported,
  normalizeTryOnHex,
  parseTryOnShades,
  resolveSingleSkuTones,
  tryOnShadeToRenderFields
} from './shop-product-try-on.util';

type TryOnRowForm = FormGroup<{
  id: FormControl<number>;
  label: FormControl<string>;
  updatedAt: FormControl<string | null>;
  enabled: FormControl<boolean>;
  color: FormControl<string | null>;
  finish: FormControl<TryOnFinish | null>;
  intensity: FormControl<number | null>;
  calibration: FormControl<TryOnCalibration | null>;
}>;

type TryOnShadeForm = FormGroup<{
  slug: FormControl<string | null>;
  name: FormControl<string | null>;
  color: FormControl<string | null>;
  finish: FormControl<TryOnFinish | null>;
  intensity: FormControl<number | null>;
  calibration: FormControl<TryOnCalibration | null>;
}>;

/**
 * Editor of the MaIA Try-On attributes (product + tones). Products with
 * variants edit one row per variant (`rows`), each with its own tone switch
 * (`try_on_enabled` of the variant row). Single-SKU products edit a single
 * list of tones (`shades`) and the product toggle is the only switch: one
 * tone is stored in the legacy `variant_try_on_*` columns of the product row,
 * two or more in `try_on_shades` (first one mirrored into the legacy columns).
 */
@Component({
  selector: 'app-shop-product-try-on-form',
  templateUrl: './shop-product-try-on-form.component.html',
  styleUrl: './shop-product-try-on-form.component.scss'
})
export class ShopProductTryOnFormComponent implements OnInit {
  @Input({ required: true }) productId: string | number;
  @Input() hasVariants = false;
  @Input() permission = false;
  @Output() saved = new EventEmitter<void>();

  private shopProductService = inject(ShopProductService);
  private messageService = inject(MessageService);
  private destroyRef = inject(DestroyRef);

  readonly categoryOptions = getTryOnCategoryOptions();
  readonly calibrationOptions = TRY_ON_CALIBRATION_OPTIONS;
  /**
   * The mode selector needs the live SDK delivered and only matters while the
   * try-on is shown on the PDP; hidden, the stored mode is kept as is.
   */
  isModeVisible = false;
  /** Finish and mode choices depend on the category; kept as properties for PrimeNG inputs. */
  finishOptions: TryOnOption<TryOnFinish>[] = [];
  modeOptions: TryOnOption<TryOnMode>[] = getTryOnModeOptionsForCategory(null);
  isLiveOnlyCategory = false;
  readonly intensityMin = TRY_ON_INTENSITY_MIN;
  readonly intensityMax = TRY_ON_INTENSITY_MAX;
  readonly shadesMax = TRY_ON_SHADES_MAX;
  readonly shadeNameMax = TRY_ON_SHADE_NAME_MAX;
  readonly defaultDisclaimer = 'Cor simulada pode diferir do produto real.';

  form = new FormGroup({
    category: new FormControl<TryOnCategory | null>(null),
    enabled: new FormControl<boolean>(false, { nonNullable: true }),
    mode: new FormControl<TryOnMode>(TRY_ON_DEFAULT_MODE, {
      nonNullable: true
    }),
    disclaimer: new FormControl<string | null>(null, [
      Validators.maxLength(240)
    ]),
    rows: new FormArray<TryOnRowForm>([]),
    shades: new FormArray<TryOnShadeForm>([])
  });

  loaded = false;
  blockReason: string | null = null;
  /** Toggle is on but the rules are not met: the save is blocked until fixed. */
  isEnableBlocked = false;
  isFinishVisible = false;
  rowErrors: string[][] = [];
  hexErrors: boolean[] = [];
  shadeErrors: string[][] = [];
  shadeHexErrors: boolean[] = [];
  shadesListErrors: string[] = [];
  /** With a single tone the name is optional (legacy columns carry none). */
  isShadeNameRequired = false;
  /** Shades still stored on a product that now has variants; cleared on save. */
  staleShadeCount = 0;
  canAddShade = false;
  /** Legacy columns as loaded; the parent row is only rewritten when they change. */
  private loadedLegacyTone: TryOnLegacyToneFields = {
    variant_try_on_color: null,
    variant_try_on_finish: null,
    variant_try_on_intensity: null,
    variant_try_on_calibration: null
  };
  productUpdatedAt: string | null = null;
  canSubmit = false;

  async ngOnInit(): Promise<void> {
    await this.load();
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshDerivedState());
    this.refreshDerivedState();
  }

  get rows(): FormArray<TryOnRowForm> {
    return this.form.controls.rows;
  }

  get shades(): FormArray<TryOnShadeForm> {
    return this.form.controls.shades;
  }

  /** The tone list only exists on single-SKU products; variant tones live on the variant rows. */
  get isShadesVisible(): boolean {
    return !this.hasVariants;
  }

  async load(): Promise<void> {
    LoaderService.showLoader();
    try {
      const fields = await this.shopProductService.getTryOnFields(
        this.productId
      );
      this.patchForm(fields);
      this.loaded = true;
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private patchForm(fields: ProductTryOnFields): void {
    this.productUpdatedAt = fields.updated_at;
    this.loadedLegacyTone = {
      variant_try_on_color: fields.variant_try_on_color,
      variant_try_on_finish: fields.variant_try_on_finish,
      variant_try_on_intensity: fields.variant_try_on_intensity,
      variant_try_on_calibration: fields.variant_try_on_calibration
    };
    this.rows.clear();
    this.hexErrors = [];
    if (this.hasVariants)
      fields.variants.forEach((row) =>
        this.rows.push(this.buildRowForm(row, true))
      );
    this.shades.clear();
    this.shadeHexErrors = [];
    // Products with variants only load shades to report/clear stale data.
    const shades = this.hasVariants
      ? parseTryOnShades(fields.try_on_shades)
      : resolveSingleSkuTones(fields, fields.try_on_shades);
    shades.forEach((shade) => this.shades.push(this.buildShadeForm(shade)));
    this.form.patchValue({
      category: fields.try_on_category,
      enabled: fields.try_on_enabled,
      mode: fields.try_on_mode ?? TRY_ON_DEFAULT_MODE,
      disclaimer: fields.try_on_disclaimer
    });
    this.form.markAsPristine();
    if (!this.permission) this.form.disable();
  }

  private buildRowForm(row: ProductTryOnRow, isVariant: boolean): TryOnRowForm {
    return new FormGroup({
      id: new FormControl<number>(row.id, { nonNullable: true }),
      label: new FormControl<string>(this.rowLabel(row), {
        nonNullable: true
      }),
      updatedAt: new FormControl<string | null>(row.updated_at),
      enabled: new FormControl<boolean>(isVariant ? row.try_on_enabled : true, {
        nonNullable: true
      }),
      color: new FormControl<string | null>(row.variant_try_on_color),
      finish: new FormControl<TryOnFinish | null>(row.variant_try_on_finish),
      intensity: new FormControl<number | null>(row.variant_try_on_intensity, [
        Validators.min(TRY_ON_INTENSITY_MIN),
        Validators.max(TRY_ON_INTENSITY_MAX)
      ]),
      calibration: new FormControl<TryOnCalibration | null>(
        row.variant_try_on_calibration
      )
    });
  }

  private rowLabel(row: ProductTryOnRow): string {
    const grade = row.grade?.map((g) => `${g.title}: ${g.value}`).join(' · ');
    return grade || row.name || row.ean || row.sku || `Variante ${row.id}`;
  }

  private buildShadeForm(shade: TryOnShadeDraft): TryOnShadeForm {
    return new FormGroup({
      slug: new FormControl<string | null>(shade.slug),
      name: new FormControl<string | null>(shade.name, [
        Validators.maxLength(TRY_ON_SHADE_NAME_MAX)
      ]),
      color: new FormControl<string | null>(shade.color),
      finish: new FormControl<TryOnFinish | null>(shade.finish),
      intensity: new FormControl<number | null>(shade.intensity, [
        Validators.min(TRY_ON_INTENSITY_MIN),
        Validators.max(TRY_ON_INTENSITY_MAX)
      ]),
      calibration: new FormControl<TryOnCalibration | null>(shade.calibration)
    });
  }

  addShade(): void {
    if (!this.canAddShade) return;
    const shade = this.buildShadeForm({
      slug: null,
      name: null,
      color: null,
      finish: null,
      intensity: null,
      calibration: null
    });
    // Dirty state first: push/removeAt emit valueChanges, which recomputes
    // canSubmit synchronously against the current dirty flag and error arrays.
    shade.markAsDirty();
    this.shades.markAsDirty();
    this.shades.push(shade);
  }

  removeShade(index: number): void {
    if (index < 0 || index >= this.shades.length) return;
    this.shadeHexErrors.splice(index, 1);
    this.shades.markAsDirty();
    this.shades.removeAt(index);
  }

  /**
   * Native colour input emits #rrggbb; store the canonical uppercase form.
   * The picker always yields a valid hex, so any pending typed-hex error of
   * that tone is cleared before valueChanges recomputes canSubmit.
   */
  onPickerChange(index: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.hexErrors[index] = false;
    this.setColor(
      this.rows.at(index).controls.color,
      normalizeTryOnHex(value) ?? null
    );
  }

  onHexBlur(index: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const normalized = normalizeTryOnHex(value);
    this.hexErrors[index] = normalized === undefined;
    if (normalized === undefined) {
      this.refreshCanSubmit();
      return;
    }
    this.setColor(this.rows.at(index).controls.color, normalized);
  }

  onShadePickerChange(index: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.shadeHexErrors[index] = false;
    this.setColor(
      this.shades.at(index).controls.color,
      normalizeTryOnHex(value) ?? null
    );
  }

  onShadeHexBlur(index: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const normalized = normalizeTryOnHex(value);
    this.shadeHexErrors[index] = normalized === undefined;
    if (normalized === undefined) {
      this.refreshCanSubmit();
      return;
    }
    this.setColor(this.shades.at(index).controls.color, normalized);
  }

  /**
   * Marks dirty before setValue: valueChanges recomputes canSubmit
   * synchronously and must already see the form as dirty.
   */
  private setColor(
    control: FormControl<string | null>,
    color: string | null
  ): void {
    control.markAsDirty();
    control.markAsTouched();
    control.setValue(color);
  }

  refreshDerivedState(): void {
    const category = this.form.controls.category.value;
    this.isFinishVisible = isTryOnFinishRequired(category);
    this.finishOptions = getTryOnFinishOptionsForCategory(category);
    this.modeOptions = getTryOnModeOptionsForCategory(category);
    this.isLiveOnlyCategory = !!category && !isTryOnPhotoSupported(category);
    this.syncModeWithCategory(category);
    this.clearIncompatibleFinishes(category);
    const product = this.productFields();
    const variantRows = this.renderRows();
    const shades = this.shadeDrafts();
    this.rowErrors = variantRows.map((row) => getTryOnRowErrors(row, product));
    this.isShadeNameRequired = shades.length > 1;
    this.shadeErrors = this.hasVariants
      ? []
      : shades.map((shade) =>
          getTryOnShadeErrors(shade, category, this.isShadeNameRequired)
        );
    this.shadesListErrors = this.hasVariants
      ? []
      : getTryOnShadesListErrors(shades);
    this.staleShadeCount = this.hasVariants ? shades.length : 0;
    this.canAddShade =
      this.permission && canAddTryOnShade(shades.length, this.hasVariants);
    this.blockReason = getTryOnEnableBlockReason(
      product,
      this.hasVariants ? variantRows : shades.map(tryOnShadeToRenderFields)
    );

    // The toggle is never flipped here: a transient state (e.g. a shade just
    // added and still empty) must not destroy the admin's choice. While the
    // rules block an enabled toggle, the save is blocked instead and the
    // reason is shown; a disabled toggle cannot be turned on until they pass.
    const enabledControl = this.form.controls.enabled;
    this.isModeVisible = TRY_ON_LIVE_ENABLED && enabledControl.value;
    this.isEnableBlocked = !!this.blockReason && enabledControl.value;
    if (this.permission) {
      const shouldDisable = !!this.blockReason && !enabledControl.value;
      if (shouldDisable && enabledControl.enabled)
        enabledControl.disable({ emitEvent: false });
      else if (!shouldDisable && enabledControl.disabled)
        enabledControl.enable({ emitEvent: false });
    }
    this.refreshCanSubmit();
  }

  /** Categories the photo API cannot render only offer the live mode. */
  private syncModeWithCategory(category: TryOnCategory | null): void {
    const control = this.form.controls.mode;
    const coerced = coerceTryOnMode(category, control.value);
    if (coerced !== control.value) {
      control.setValue(coerced, { emitEvent: false });
      control.markAsDirty();
    }
  }

  /** A finish that the new category does not offer is dropped from the tone. */
  private clearIncompatibleFinishes(category: TryOnCategory | null): void {
    const finishControls = [
      ...this.rows.controls.map((row) => row.controls.finish),
      ...this.shades.controls.map((shade) => shade.controls.finish)
    ];
    for (const control of finishControls) {
      if (
        control.value !== null &&
        !isTryOnFinishAllowed(category, control.value)
      ) {
        control.setValue(null, { emitEvent: false });
        control.markAsDirty();
      }
    }
  }

  private productFields() {
    const raw = this.form.getRawValue();
    return {
      category: raw.category,
      enabled: raw.enabled,
      mode: raw.mode,
      disclaimer: raw.disclaimer
    };
  }

  private renderRows(): TryOnRenderFields[] {
    return this.form.getRawValue().rows.map((row) => ({
      enabled: row.enabled,
      color: row.color,
      finish: row.finish,
      intensity: row.intensity
    }));
  }

  private shadeDrafts(): TryOnShadeDraft[] {
    return this.form.getRawValue().shades.map((shade) => ({
      slug: shade.slug,
      name: shade.name,
      color: shade.color,
      finish: shade.finish,
      intensity: shade.intensity,
      calibration: shade.calibration
    }));
  }

  private refreshCanSubmit(): void {
    const hasRowErrors = this.rowErrors.some((errors) => errors.length > 0);
    const hasShadeErrors =
      this.shadesListErrors.length > 0 ||
      this.shadeErrors.some((errors) => errors.length > 0);
    this.canSubmit =
      this.permission &&
      this.loaded &&
      this.form.dirty &&
      this.form.valid &&
      !this.isEnableBlocked &&
      !hasRowErrors &&
      !hasShadeErrors &&
      !this.hexErrors.some(Boolean) &&
      !this.shadeHexErrors.some(Boolean);
  }

  async submit(): Promise<void> {
    if (!this.canSubmit) return;
    LoaderService.showLoader();
    try {
      if (this.hasVariants) await this.submitVariants();
      else await this.submitSingleSku();

      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Prova virtual atualizada com sucesso.'
      });
      await this.load();
      this.saved.emit();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  /**
   * Variant tones: each dirty variant row is written, then the parent row.
   * `try_on_shades` is only written to clear stale shades the parent still
   * holds; otherwise the column is left untouched.
   */
  private async submitVariants(): Promise<void> {
    const category = this.form.controls.category.value;
    const dirtyRows = this.rows.controls.filter(
      (control) => control.dirty || this.categoryChanged()
    );
    for (const control of dirtyRows) {
      const row = control.getRawValue();
      await this.shopProductService.updateTryOnRowFields(
        row.id,
        buildTryOnRowSetInput(
          category,
          {
            enabled: row.enabled,
            color: row.color,
            finish: row.finish,
            intensity: row.intensity,
            calibration: row.calibration
          },
          true
        )
      );
    }

    // Product-level fields are written last so `try_on_enabled` is evaluated
    // against the rows already persisted above.
    const shades = this.shadeDrafts();
    await this.shopProductService.updateProductTryOnFields(this.productId, {
      ...buildTryOnProductSetInput(this.productFields(), this.renderRows()),
      ...(shades.length > 0
        ? { try_on_shades: buildTryOnShadesInput(category, shades, true) }
        : {})
    });
  }

  /**
   * Single-SKU tone list: the legacy columns of the product row are written
   * whenever the values to persist differ from the ones loaded (derived from
   * stored state + current list, not from dirtiness, so a stored palette of
   * one shade reaches the legacy columns before `try_on_shades` is nulled and
   * an untouched legacy product keeps today's payload). Then the product row
   * is written with `try_on_shades` in the same `_set` as the toggle.
   */
  private async submitSingleSku(): Promise<void> {
    const category = this.form.controls.category.value;
    const shades = this.shadeDrafts();
    const tones = buildSingleSkuToneSetInputs(category, shades);

    if (
      this.categoryChanged() ||
      !isSameLegacyTone(tones.row, this.loadedLegacyTone)
    )
      await this.shopProductService.updateTryOnRowFields(
        this.productId,
        tones.row
      );

    await this.shopProductService.updateProductTryOnFields(this.productId, {
      ...buildTryOnProductSetInput(
        this.productFields(),
        shades.map(tryOnShadeToRenderFields)
      ),
      try_on_shades: tones.shades
    });
  }

  /** Changing the category re-evaluates every tone (incompatible finishes are cleared). */
  private categoryChanged(): boolean {
    return this.form.controls.category.dirty;
  }

  /**
   * `try_on_shades` is written on single-SKU products (the editor is visible)
   * and on a product with variants only to clear stale shades it still holds;
   * otherwise the column is left untouched.
   */
  private shouldWriteShades(shades: TryOnShadeDraft[]): boolean {
    return !this.hasVariants || shades.length > 0;
  }
}
